import { useEffect, useRef, useCallback } from 'react';

/* ── AUDIO ENGINE ── */
let AC = null;
function getAC() {
  if (!AC) {
    try { AC = new (window.AudioContext || window.webkitAudioContext)(); } catch(e) {}
  }
  return AC;
}
function mkGain(v = .045) {
  const ac = getAC(); if (!ac) return null;
  const g = ac.createGain(); g.gain.value = v; g.connect(ac.destination); return g;
}

export const sounds = {
  laser() {
    const g = mkGain(.045); if (!g) return;
    const ac = getAC();
    const o = ac.createOscillator(), w = ac.createWaveShaper();
    const c = new Float32Array(256);
    for (let i = 0; i < 256; i++) { const x = i * 2 / 256 - 1; c[i] = x * (Math.abs(x) + .2) / 1.2; }
    w.curve = c; o.type = 'sawtooth';
    o.frequency.setValueAtTime(600, ac.currentTime);
    o.frequency.exponentialRampToValueAtTime(80, ac.currentTime + .13);
    g.gain.exponentialRampToValueAtTime(.001, ac.currentTime + .15);
    o.connect(w); w.connect(g); o.start(); o.stop(ac.currentTime + .17);
  },
  blip() {
    const g = mkGain(.028); if (!g) return;
    const ac = getAC();
    const o = ac.createOscillator(); o.type = 'sine'; o.frequency.value = 900;
    g.gain.exponentialRampToValueAtTime(.001, ac.currentTime + .045);
    o.connect(g); o.start(); o.stop(ac.currentTime + .055);
  },
  select() {
    const ac = getAC(); if (!ac) return;
    [340, 520].forEach((f, i) => {
      const g = mkGain(0), o = ac.createOscillator();
      o.type = 'square'; o.frequency.value = f;
      const t = ac.currentTime + i * .04;
      g.gain.linearRampToValueAtTime(.055, t + .01);
      g.gain.exponentialRampToValueAtTime(.001, t + .09);
      o.connect(g); o.start(t); o.stop(t + .11);
    });
  },
  power() {
    const ac = getAC(); if (!ac) return;
    [261, 329, 392, 523].forEach((f, i) => {
      const g = mkGain(0), o = ac.createOscillator();
      o.type = 'square'; o.frequency.value = f;
      const t = ac.currentTime + i * .048;
      g.gain.linearRampToValueAtTime(.05, t + .01);
      g.gain.exponentialRampToValueAtTime(.001, t + .13);
      o.connect(g); o.start(t); o.stop(t + .15);
    });
  },
  resume() { getAC()?.resume(); }
};

/* ── BURST ENGINE ── */
let burstCanvas = null, burstCtx = null, burstParts = [], burstRunning = false;
function initBurst() {
  if (burstCanvas) return;
  burstCanvas = document.getElementById('dm-burst-canvas');
  if (!burstCanvas) return;
  burstCtx = burstCanvas.getContext('2d');
  burstCanvas.width = window.innerWidth;
  burstCanvas.height = window.innerHeight;
  window.addEventListener('resize', () => {
    if (burstCanvas) { burstCanvas.width = window.innerWidth; burstCanvas.height = window.innerHeight; }
  });
  if (!burstRunning) { burstRunning = true; animBurst(); }
}
function animBurst() {
  if (!burstCtx) return;
  burstCtx.clearRect(0, 0, burstCanvas.width, burstCanvas.height);
  burstParts = burstParts.filter(p => p.life > 0);
  burstParts.forEach(p => {
    p.x += p.vx; p.y += p.vy; p.vx *= .93; p.vy *= .93; p.life -= .04;
    burstCtx.globalAlpha = p.life; burstCtx.fillStyle = p.col;
    burstCtx.beginPath();
    if (p.shard) { burstCtx.fillRect(p.x, p.y, p.r * .5, p.r * 2.4); }
    else { burstCtx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2); }
    burstCtx.fill();
  });
  burstCtx.globalAlpha = 1;
  requestAnimationFrame(animBurst);
}

export function burst(x, y, col = '#E31937', n = 14) {
  initBurst();
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2, s = 1.2 + Math.random() * 4;
    burstParts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, col, r: 1.2 + Math.random() * 1.8, shard: Math.random() > .6 });
  }
}

/* ── CONFETTI / CELEBRATION ── */
export function celebrate(text = '🎉 BRIEF WON!') {
  sounds.resume();
  sounds.power();
  // Multi-burst confetti from multiple points
  const colors = ['#22C55E', '#EAB308', '#06B6D4', '#A855F7', '#EC4899', '#E31937'];
  const W = window.innerWidth, H = window.innerHeight;
  [W*.2, W*.5, W*.8].forEach((x, idx) => {
    setTimeout(() => {
      for (let i = 0; i < 30; i++) {
        const col = colors[Math.floor(Math.random() * colors.length)];
        burst(x, H * .3, col, 1);
      }
    }, idx * 120);
  });
  // Golden flash
  flashScreen('rgba(234,179,8,.08)');
  // Show celebration toast
  showToast(text, 'celebration');
}

export function flashScreen(col = 'rgba(227,25,55,.05)') {
  const f = document.getElementById('dm-flash');
  if (!f) return;
  f.style.background = col; f.style.opacity = '1';
  setTimeout(() => f.style.opacity = '0', 65);
}

/* ── XP SYSTEM ── */
let dmXP = 0, dmLevel = 1;
export function addXP(pts) {
  dmXP += pts;
  if (dmXP >= 100) { dmXP = dmXP % 100; dmLevel++; sounds.power(); updateLevelBadge(); }
  const fill = document.getElementById('dm-xp-fill');
  if (fill) fill.style.width = dmXP + '%';
}
function updateLevelBadge() {
  const el = document.getElementById('dm-level-badge');
  if (el) el.textContent = 'LVL ' + dmLevel;
}
export function getLevel() { return dmLevel; }

/* ── COMBO SYSTEM ── */
let dmCombo = 0, dmComboTimer = null;
export function addCombo() {
  dmCombo++;
  const c = document.getElementById('dm-combo');
  const s = document.getElementById('dm-combo-sub');
  if (!c || !s) return;
  c.textContent = 'x' + dmCombo;
  c.classList.add('dm-combo-on'); s.classList.add('dm-combo-on');
  c.style.transform = 'scale(1.2)'; setTimeout(() => c.style.transform = '', 150);
  clearTimeout(dmComboTimer);
  dmComboTimer = setTimeout(() => {
    c.classList.remove('dm-combo-on'); s.classList.remove('dm-combo-on'); dmCombo = 0;
  }, 1700);
}

/* ── TOAST ── */
let toastTimer = null;
export function showToast(msg, type = '') {
  const t = document.getElementById('dm-toast');
  if (!t) return;
  t.textContent = msg; t.className = 'show ' + type;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.className = '', 2300);
}

/* ── COUNT UP ── */
export function countUp(el, target, dur = 800) {
  if (!el) return;
  const start = Date.now();
  const tick = () => {
    const p = Math.min(1, (Date.now() - start) / dur);
    const ease = 1 - Math.pow(1 - p, 3);
    el.textContent = Math.round(target * ease);
    if (p < 1) requestAnimationFrame(tick);
  };
  tick();
}

/* ── NAME SCRAMBLE ── */
const AB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
export function scramble(el, final, ms = 820) {
  if (!el) return;
  const arr = final.split(''); let f = 0; const tot = Math.floor(ms / 35);
  const id = setInterval(() => {
    el.textContent = arr.map((c, i) => {
      if (f > (i / arr.length) * tot * .75) return c;
      return AB[Math.floor(Math.random() * AB.length)];
    }).join('');
    if (++f > tot) { el.textContent = final; clearInterval(id); }
  }, 35);
}

/* ── SVG SPARKLINE ── */
export function mkSparkSVG(svgEl, vals, col) {
  if (!svgEl) return;
  const W = svgEl.clientWidth || 100, H = 24;
  const mx = Math.max(...vals), mn = Math.min(...vals), range = mx - mn || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${H - ((v - mn) / range) * (H - 4) - 2}`).join(' ');
  const areapts = `0,${H} ${pts} ${W},${H}`;
  const uid = col.replace('#', '');
  svgEl.innerHTML = `
    <defs><linearGradient id="sg${uid}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${col}" stop-opacity=".25"/>
      <stop offset="100%" stop-color="${col}" stop-opacity="0"/>
    </linearGradient></defs>
    <polygon points="${areapts}" fill="url(#sg${uid})"/>
    <polyline points="${pts}" fill="none" stroke="${col}" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" opacity=".7"/>
  `;
}

/* ── 3D TILT ── */
export function applyTilt(e, el) {
  if (!window.matchMedia('(hover: hover)').matches) return;
  const r = el.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width - .5;
  const y = (e.clientY - r.top) / r.height - .5;
  el.style.setProperty('--rx', (-y * 4) + 'deg');
  el.style.setProperty('--ry', (x * 6) + 'deg');
  el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
  el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100) + '%');
}
export function resetTilt(el) {
  el.style.setProperty('--rx', '0deg');
  el.style.setProperty('--ry', '0deg');
}

/* ── TOUCH DETECTION ── */
export const isTouch = () => !window.matchMedia('(hover: hover)').matches;
export const isMobile = () => window.innerWidth <= 768;

/* ── PITCH COUNTDOWN ── */
export function getDaysUntil(dateStr) {
  const target = new Date(dateStr);
  const now = new Date();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

/* ── GREETING BY TIME ── */
export function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Good night,';
  if (h < 12) return 'Good morning,';
  if (h < 17) return 'Good afternoon,';
  if (h < 21) return 'Good evening,';
  return 'Good night,';
}

/* ── WELLNESS ADVICE (rotates each visit) ── */
const WELLNESS_TIPS = [
  "Take a 5-minute walk before diving in. Your body will thank you.",
  "Breathe in for 4, hold for 7, exhale for 8. Repeat 3 times.",
  "Remember: urgent and important are different things.",
  "Drink a glass of water before your first coffee.",
  "What are you grateful for today? Name one thing.",
  "Your posture right now — straighten up, roll those shoulders back.",
  "The best ideas come after rest, not after hustle.",
  "Focus on progress, not perfection. Ship it.",
  "Take your eyes off the screen. Look at something 20 feet away for 20 seconds.",
  "You don't have to do everything today. Pick 3 things that matter.",
  "Stretch your wrists and fingers. They carry your work.",
  "A cluttered desk is a cluttered mind. Clear one thing.",
  "Silence is productive. Not every gap needs to be filled.",
  "Ask yourself: will this matter in a week? If not, let it go.",
  "Move your body for 2 minutes. Jumping jacks, stretches, anything.",
  "Your energy is finite. Protect it from low-value meetings.",
  "Eat something real. Your brain runs on food, not willpower.",
  "Call someone you care about today. Connection is fuel.",
  "Write down one thing you accomplished yesterday. You did more than you think.",
  "The sun is out somewhere. Step outside for 60 seconds if you can.",
  "Not every problem needs a solution right now. Some just need patience.",
  "Deep work happens in blocks. Protect a 90-minute window today.",
  "Inbox zero is a trap. Focus on output, not input.",
  "Smile. Even if forced, it shifts your chemistry.",
  "Your team needs your presence more than your productivity.",
  "Sleep is not a reward. It's a requirement. Aim for 7+ hours tonight.",
  "What would you tell a friend in your situation? Take your own advice.",
  "One kind word to a colleague can change their whole day.",
  "Take a screenshot of your to-do list. Cross off the bottom half. Focus on the top.",
  "You are not your job title. You are a human having a work experience.",
  "If everything is a priority, nothing is. Choose wisely.",
  "Celebrate small wins. They compound into big ones.",
  "Your phone doesn't need to be in your hand right now.",
  "Listen to one song that makes you feel alive before you start.",
  "The best leaders know when to pause. This might be that moment.",
  "You've handled hard days before. Today is no different.",
  "Hydrate. Seriously. Go get water right now.",
  "The work will still be here tomorrow. You don't have to finish it all today.",
  "Take a moment to notice 3 things you can see, 2 you can hear, 1 you can feel.",
  "Comparison is the thief of joy. Run your own race.",
];

export function getWellnessTip() {
  // Use date + hour as seed so it changes throughout the day but is stable within the hour
  const now = new Date();
  const seed = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate() + now.getHours();
  return WELLNESS_TIPS[seed % WELLNESS_TIPS.length];
}

/* ── HOOK ── */
export function useDMEngine() {
  // Cursor trail removed in Sprint 1 UX cleanup
  const setCursorHover = useCallback((on) => {
    document.body.classList.toggle("dm-cursor-hover", on);
  }, []);
  return { setCursorHover };
}
